import React, { useState } from 'react';
import { DeckSlideWrapper } from '../DeckSlideWrapper';
import { Building2, Cloud, Sparkles, Check, Calculator } from 'lucide-react';

export const Slide06BusinessModel: React.FC = () => {
  const [enterpriseSeats, setEnterpriseSeats] = useState<number>(20);

  const calculateARR = (clients: number) => {
    const enterpriseARR = clients * 36000;
    const saasARR = clients * 12 * 499 * 4;
    return (enterpriseARR + saasARR).toLocaleString();
  };

  return (
    <DeckSlideWrapper
      slideNumber={6}
      totalSlides={12}
      category="Business Model"
      title="Dual-Engine Monetization &amp; High Gross Margins"
      subtitle="Capturing high-ACV enterprise contracts while driving bottom-up SaaS developer adoption."
    >
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 sm:gap-4 mb-2.5 sm:mb-3">
        {/* Tier 1: Self-Hosted Enterprise */}
        <div className="p-3.5 sm:p-4.5 rounded-2xl bg-white/90 dark:bg-navy-850/80 border-2 border-gold-500/50 dark:border-gold-500/40 shadow-sm relative flex flex-col justify-between">
          <div className="absolute top-3 right-3 px-2 py-0.5 rounded-full text-[10px] font-mono font-bold bg-gold-500/20 text-gold-800 dark:text-gold-400">
            HIGH ACV DRIVER
          </div>
          <div>
            <div className="flex items-center gap-1.5 text-gold-700 dark:text-gold-400 font-mono text-[11px] sm:text-xs font-bold mb-1">
              <Building2 className="w-4 h-4" />
              <span>STREAM 01 // ENTERPRISE</span>
            </div>
            <h3 className="text-sm sm:text-base font-bold text-slate-900 dark:text-white">
              Self-Hosted License
            </h3>
            <div className="my-1.5 sm:my-2">
              <span className="text-xl sm:text-2xl font-extrabold text-slate-900 dark:text-white font-mono">$24K – $120K</span>
              <span className="text-xs text-slate-500 font-mono"> / year</span>
            </div>
            <ul className="space-y-1.5 text-xs text-slate-600 dark:text-slate-300 mt-1.5">
              <li className="flex items-center gap-1.5">
                <Check className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
                <span>Air-gapped on-prem Docker &amp; K8s</span>
              </li>
              <li className="flex items-center gap-1.5">
                <Check className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
                <span>SAML 2.0 / LDAP Identity Federation</span>
              </li>
              <li className="flex items-center gap-1.5">
                <Check className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
                <span>Dedicated SLA &amp; Compliance Vault</span>
              </li>
            </ul>
          </div>
          <div className="mt-2.5 pt-2 border-t border-slate-200 dark:border-navy-700 text-[10px] sm:text-[11px] font-mono text-slate-500">
            Target: Banks, Healthcare, Defence, Enterprises
          </div>
        </div>

        {/* Tier 2: Managed Cloud SaaS */}
        <div className="p-3.5 sm:p-4.5 rounded-2xl bg-white/90 dark:bg-navy-850/80 border border-slate-200 dark:border-navy-700 shadow-sm flex flex-col justify-between">
          <div>
            <div className="flex items-center gap-1.5 text-blue-600 dark:text-blue-400 font-mono text-[11px] sm:text-xs font-bold mb-1">
              <Cloud className="w-4 h-4" />
              <span>STREAM 02 // CLOUD SAAS</span>
            </div>
            <h3 className="text-sm sm:text-base font-bold text-slate-900 dark:text-white">
              Managed Edge Subscriptions
            </h3>
            <div className="my-1.5 sm:my-2">
              <span className="text-xl sm:text-2xl font-extrabold text-slate-900 dark:text-white font-mono">$99 – $499</span>
              <span className="text-xs text-slate-500 font-mono"> / month + usage</span>
            </div>
            <ul className="space-y-1.5 text-xs text-slate-600 dark:text-slate-300 mt-1.5">
              <li className="flex items-center gap-1.5">
                <Check className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
                <span>Instant sign-up via LemonSqueezy</span>
              </li>
              <li className="flex items-center gap-1.5">
                <Check className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
                <span>Global Cloudflare Edge low-latency</span>
              </li>
              <li className="flex items-center gap-1.5">
                <Check className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
                <span>Metered token overage billing</span>
              </li>
            </ul>
          </div>
          <div className="mt-2.5 pt-2 border-t border-slate-200 dark:border-navy-700 text-[10px] sm:text-[11px] font-mono text-slate-500">
            Target: High-growth tech startups &amp; mid-market
          </div>
        </div>

        {/* Tier 3: Custom Parsers & Services */}
        <div className="p-3.5 sm:p-4.5 rounded-2xl bg-white/90 dark:bg-navy-850/80 border border-slate-200 dark:border-navy-700 shadow-sm flex flex-col justify-between">
          <div>
            <div className="flex items-center gap-1.5 text-purple-600 dark:text-purple-400 font-mono text-[11px] sm:text-xs font-bold mb-1">
              <Sparkles className="w-4 h-4" />
              <span>STREAM 03 // ADD-ONS</span>
            </div>
            <h3 className="text-sm sm:text-base font-bold text-slate-900 dark:text-white">
              Custom Parsers &amp; Add-ons
            </h3>
            <div className="my-1.5 sm:my-2">
              <span className="text-xl sm:text-2xl font-extrabold text-slate-900 dark:text-white font-mono">$10K – $25K</span>
              <span className="text-xs text-slate-500 font-mono"> one-time</span>
            </div>
            <ul className="space-y-1.5 text-xs text-slate-600 dark:text-slate-300 mt-1.5">
              <li className="flex items-center gap-1.5">
                <Check className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
                <span>Custom Haskell Parsec doc formats</span>
              </li>
              <li className="flex items-center gap-1.5">
                <Check className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
                <span>Proprietary PII/NER regex authoring</span>
              </li>
              <li className="flex items-center gap-1.5">
                <Check className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
                <span>Dedicated migration &amp; audit</span>
              </li>
            </ul>
          </div>
          <div className="mt-2.5 pt-2 border-t border-slate-200 dark:border-navy-700 text-[10px] sm:text-[11px] font-mono text-slate-500">
            High margin services converted to annual ARR
          </div>
        </div>
      </div>

      {/* Unit Economics & Interactive ARR Bar */}
      <div className="p-3 sm:p-3.5 rounded-xl bg-slate-900 dark:bg-navy-950 text-slate-200 border border-slate-700 dark:border-navy-700 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-2.5 sm:gap-3 text-xs font-mono">
        <div className="flex flex-wrap items-center gap-3 sm:gap-4">
          <div className="flex items-center gap-1.5">
            <span className="text-gold-400 font-bold text-sm">88%+</span>
            <span className="text-slate-400">Gross Margin</span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="text-emerald-400 font-bold text-sm">135%+</span>
            <span className="text-slate-400">NRR (Volume Expansion)</span>
          </div>
        </div>

        <div className="flex items-center justify-between sm:justify-end gap-2 bg-slate-950 dark:bg-navy-900 px-3 py-1.5 rounded-lg border border-slate-800 dark:border-navy-800">
          <div className="flex items-center gap-1.5">
            <Calculator className="w-3.5 h-3.5 text-gold-400" />
            <span className="text-slate-400 text-[11px]">ARR at</span>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setEnterpriseSeats((prev) => Math.max(5, prev - 5))}
                className="w-5 h-5 rounded bg-slate-800 dark:bg-navy-800 hover:bg-slate-700 text-white flex items-center justify-center font-bold text-xs"
              >
                -
              </button>
              <span className="font-bold text-gold-400 px-1">{enterpriseSeats}</span>
              <button
                onClick={() => setEnterpriseSeats((prev) => Math.min(100, prev + 5))}
                className="w-5 h-5 rounded bg-slate-800 dark:bg-navy-800 hover:bg-slate-700 text-white flex items-center justify-center font-bold text-xs"
              >
                +
              </button>
            </div>
            <span className="text-slate-400 text-[11px] hidden xs:inline">Clients:</span>
          </div>
          <strong className="text-white font-bold">${calculateARR(enterpriseSeats)}</strong>
        </div>
      </div>
    </DeckSlideWrapper>
  );
};
